<template>
  <div>
    <div v-for="({ item, amount }, index) in cartProductContent" :key="item.id + index">
      <div class="row my-4">
        <div class="col-4 col-sm-3 col-md-3">
          <ProductImage
            :class="'cursor-pointer'"
            :images="[item.image]"
            :icons="getIconsForProduct(item)"
            @click="showProductDetail(item.id)"
            :product="item"
          />
        </div>
        <div class="col-7 col-sm-8 col-md-8">
          <p>{{ item.title }}</p>
          <div
            class="d-flex justify-content-between align-items-center"
            :class="[item.multiple ? 'mb-2' : 'mb-4']"
          >
            <div>
              <span class="fw-semibold">Codigo: {{ item.code }}</span>
            </div>
            <div>
              <span class="fw-semibold">Clave: {{ item.key }}</span>
            </div>
          </div>
          <p class="fw-semibold mb-0 mb-2" v-if="item.multiple">
            {{ `${getMultiplePriceFormatted(item)}` }}
            <font-awesome-icon icon="fa-solid fa-box" class="ms-2" />
            {{ `x ${item.multiple} = ${getUnitPriceFormatted(item)}` }}
          </p>
          <div class="row align-items-center z-1">
            <div class="col-12">
              <p v-if="item.multiple" class="price-label">
                <font-awesome-icon icon="fa-solid fa-box" class="me-2" />
                {{ getMultipleLineTotal(item, amount) }}
              </p>
              <QuantitySelector
                v-model="cartProductContent[index].amount"
                :id="item.id"
                @add="addOne"
                @subtract="subtractOne"
                @change="changeAmount"
                class="my-2"
              />
            </div>
          </div>
          <ProductPriceSummary :product="item" :amount="amount" />
        </div>
        <div class="col-1">
          <font-awesome-icon
            class="cursor-pointer text-app"
            icon="fa-solid fa-trash"
            size="lg"
            @click="removeProduct(index)"
          />
        </div>
      </div>
      <hr v-if="index + 1 < cartProductContent.length" />
    </div>

    <div v-if="!hideOriginalFooter">
      <CartSideMenuFooter
        :configFooterContent="footerContent"
        :classFooter="'z-1'"
        noBtnContinue
        splitLineAfterTotal
      />
    </div>
  </div>
</template>

<script setup>
import { useShoppingStore } from '@/stores/shopping'
import { useUserStore } from '@/stores/user'
import { useCartConfigStore } from '@/stores/config/cart'
import { computed, toRefs, getCurrentInstance } from 'vue'
import CartSideMenuFooter from '@/components/layout/cart/CartSideMenuFooter.vue'
import ProductPriceSummary from '@/components/products/ProductPriceSummary.vue'
import ProductImage from '@/components/products/ProductImage.vue'
import QuantitySelector from '@/components/shared/QuantitySelector.vue'
import { usePriceKey } from '@/composables/usePriceKey'
import { useSiteConfigStore } from '@/stores/config/site'
import { insertAtPosition } from '@/utils/arrayHelpers'

const { priceKey, shouldApplyDynamicPrice } = usePriceKey()

const getCurrentPrice = (item) => item?.[priceKey.value] || item?.price

/**
 * hideOriginalFooter: Controla si se muestra el footer del carrito.
 * Se usa en componentes que reusan ProductCartList pero no necesitan mostrar el footer (ej. CartSideMenu).
 */
const props = defineProps({
  hideOriginalFooter: { type: Boolean, required: false, default: false }
})

const images = import.meta.globEager('../../assets/images/shared/marcas/*.svg')
const capitalizedBrandName = (productBrandName) => {
  if (!productBrandName) return ''
  return productBrandName.charAt(0).toUpperCase() + productBrandName.slice(1)
}

function getBrandImage(brandName) {
  if (!brandName) return null
  const getBrandName = capitalizedBrandName(brandName)
  const image = images[`../../assets/images/shared/marcas/${getBrandName}.svg`]
  return image ? image.default : null
}

const { hideOriginalFooter } = toRefs(props)

const emit = defineEmits([
  'subtractOne',
  'addOne',
  'changeAmount',
  'removeProduct',
  'showProductDetail'
])

const globalProperties = getCurrentInstance().appContext.config.globalProperties
const formatCurrency = globalProperties.$formatcurrency

const shoppingStore = useShoppingStore()
const userStore = useUserStore()
const siteConfigStore = useSiteConfigStore()
const productImageConfig = siteConfigStore.getProductConfigImage

const isActiveClientAddress = computed(() => {
  return siteConfigStore?.getShoppingOptions?.enableClientAddresses
})

const hasChargeDelivery = computed(() => {
  return userStore?.addressSelectedHasCharge
})

const chargeDelivery = computed(() => {
  return userStore?.addressSelectedChargeAmount
})

const agreementData = computed(() => userStore.getAgreementData)
const cartConfigStore = useCartConfigStore()

const sideCartMenuConfig = computed(() => cartConfigStore.getSideCartMenuConfig)
const footerValues = computed(() => sideCartMenuConfig?.value?.footerValues)
const creditLogicState = computed(
  () => isLogicCreditBalanceActive.value && getBalanceLogicState.value
)
const isLogicCreditBalanceActive = computed(
  () => cartConfigStore.getIsLogicCreditBalanceByUserActive
)

const footerContent = computed(() => {
  const content = []
  let configData = footerValues.value // arreglo en el orden de la marca

  if (
    creditLogicState.value &&
    (!agreementData.value?.agreementType || agreementData.value?.agreementType === 1)
  ) {
    configData = ['userBalance', 'subtotal', 'discount', 'total']
  } else {
    configData = footerValues.value
  }

  const isDynamicPrice = shouldApplyDynamicPrice.value
  let oldTotal =
    creditLogicState.value &&
    (!agreementData.value?.agreementType || agreementData.value?.agreementType === 1)
      ? shoppingStore.cartTotalPrice - userBalance.value
      : shoppingStore.cartTotalPrice
  let newTotal =
    creditLogicState.value &&
    (!agreementData.value?.agreementType || agreementData.value?.agreementType === 1)
      ? shoppingStore.cartTotalPriceWithPricingRules - userBalance.value
      : shoppingStore.cartTotalPriceWithPricingRules
  if (isActiveClientAddress.value && hasChargeDelivery.value) {
    oldTotal += chargeDelivery.value
    newTotal += chargeDelivery.value
    configData = insertAtPosition(configData, -2, 'chargeDelivery')
  }
  const oldDiscount =
    creditLogicState.value &&
    (!agreementData.value?.agreementType || agreementData.value?.agreementType === 1)
      ? shoppingStore.cartTotalDiscount + (userBalance.value - remainingBalance.value)
      : shoppingStore.cartTotalDiscount
  const newDiscount =
    creditLogicState.value &&
    (!agreementData.value?.agreementType || agreementData.value?.agreementType === 1)
      ? shoppingStore.cartTotalDiscountWithPricingRules +
        (userBalance.value - remainingBalance.value)
      : shoppingStore.cartTotalDiscountWithPricingRules

  configData.forEach((key) => {
    // ... armar el objeto para cada label como lo haces actualmente
    let item = null

    if (key === 'subtotal') {
      item = {
        label: 'Subtotal',
        value: formatCurrency(shoppingStore.cartSubTotalPrice)
      }
    } else if (key === 'discount') {
      if (!isDynamicPrice && oldDiscount > 0) {
        item = {
          label: 'Descuento',
          classLabel: 'text-success',
          classValue: 'text-success',
          value: formatCurrency(oldDiscount)
        }
      }

      // Muestra el descuento dinámico si aplica
      if (isDynamicPrice && newDiscount > 0) {
        item = {
          label: 'Descuento',
          classLabel: 'text-success',
          classValue: 'text-success',
          value: formatCurrency(newDiscount)
        }
      }
    } else if (key === 'total') {
      // Si aplica la regla, agregas el nuevo total dinámico aquí mismo
      if (isDynamicPrice && oldTotal !== newTotal) {
        // Primero el total original
        item = {
          label: 'Total a pagar',
          isTotal: true,
          classLabel: isDynamicPrice
            ? 'fw-bold text-muted text-decoration-line-through'
            : 'fw-bold',
          classValue: isDynamicPrice
            ? 'fw-bold text-muted text-decoration-line-through'
            : 'fw-bold',
          value: formatCurrency(oldTotal < 0 ? 0 : oldTotal),
          showStrikethrough: isDynamicPrice
        }
        content.push(item)
        content.push({
          label: 'Total con promoción',
          isTotal: true,
          classLabel: 'fw-bold text-success',
          classValue: 'fw-bold text-success',
          value: formatCurrency(newTotal < 0 ? 0 : newTotal),
          highlight: true
        })
        return // Ya agregaste ambos, no sigas con este ciclo
      } else {
        // Total normal
        content.push({
          label: 'Total a pagar',
          isTotal: true,
          classLabel: 'fw-bold',
          classValue: 'fw-bold',
          value: formatCurrency(oldTotal)
        })
        return
      }
    } else if (key === 'userBalance') {
      item = {
        label: 'Saldo de bono',
        value: formatCurrency(remainingBalance.value)
      }
    } else if (key === 'chargeDelivery' && isActiveClientAddress.value && hasChargeDelivery.value) {
      item = {
        label: 'Cargo por entrega',
        value: formatCurrency(chargeDelivery.value)
      }
    }

    if (item) content.push(item)
  })

  return content
})

const userBalance = computed({
  get: () => shoppingStore.getUserBalance,
  set: (newValue) => {
    shoppingStore.updateUserBalance(newValue)
  }
})
const remainingBalance = computed(() => shoppingStore.getUserBalanceMinusTotal)
const getBalanceLogicState = computed(() => shoppingStore.getBalanceLogicState)
const cartProductContent = computed(() => {
  return shoppingStore.cartContent
})

const getIconsForProduct = (product) => {
  const icons = [
    {
      type: 'image',
      src: getBrandImage(product.brand), // Usar la imagen reactiva
      alt: 'Brand Image',
      class: 'img-fluid brand-image-width-card',
      position: productImageConfig?.brandIcon?.position,
      tooltip: 'Marca',
      state: productImageConfig?.brandIcon?.state // Mostrar solo si la imagen está disponible
    },
    {
      type: 'badge',
      content: 'F',
      bgClass: 'text-bg-primary',
      position: productImageConfig?.fastenalIcon?.position,
      tooltip: 'Fastenal',
      state: productImageConfig?.fastenalIcon?.state && product.fastenal_part_num
    },
    {
      type: 'fontawesome',
      icon: ['fas', 'tags'],
      iconClass: 'text-danger fs-1',
      text: `${product.discount}%`,
      textClass: 'text-white',
      transform: 'shrink-13 up-1 down-1',
      position: productImageConfig?.discountTag?.position,
      tooltip: `${product.discount}% de descuento`,
      state: productImageConfig?.discountTag?.state && Number(product.discount) > 0
    }
  ]

  return icons
}

function subtractOne(id) {
  const product = cartProductContent.value.find((p) => p.item.id === id)
  if (!product || product.amount === 1) return
  emit('subtractOne', id)
}

function addOne(id) {
  emit('addOne', id)
}

function changeAmount(id, amount) {
  emit('changeAmount', id, amount)
}

function removeProduct(index) {
  emit('removeProduct', index)
}

function showProductDetail(id) {
  emit('showProductDetail', id)
}

const getUnitPriceFormatted = (item) => formatCurrency(getCurrentPrice(item))
const getMultiplePriceFormatted = (item) => formatCurrency(getCurrentPrice(item) / item.multiple)

const getMultipleLineTotal = (item, qty) =>
  `${qty} x ${item.multiple} Total: ${item.multiple * qty}`
</script>
